import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
    FolderOpen, 
    Clock, 
    ArrowRight, 
    X,
    Crown,
    Sparkles,
    Database,
    Layout,
    Code,
    Wallet,
    FileText
} from 'lucide-react';
import { Button } from './ui/button';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Map continue_build options to icons and labels
const ACTION_CONFIG = {
    ui_design: { icon: Layout, label: 'UI Design', color: 'text-blue-400' },
    database: { icon: Database, label: 'Database Schema', color: 'text-green-400' },
    code_starter: { icon: Code, label: 'Code Starter', color: 'text-yellow-400' },
    api: { icon: FileText, label: 'API Design', color: 'text-purple-400' },
    monetization: { icon: Wallet, label: 'Monetization', color: 'text-pink-400' },
    blueprint: { icon: Sparkles, label: 'Blueprint Generated', color: 'text-violet-400' }
};

export const ResumeCard = ({ 
    onContinue, 
    onViewProjects, 
    isPro = false,
    onUpgrade,
    hitPaywall = false 
}) => {
    const { isAuthenticated, token, getAuthHeaders } = useAuth();
    const [recentProject, setRecentProject] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isDismissed, setIsDismissed] = useState(false);

    const fetchRecentProject = useCallback(async () => {
        if (!isAuthenticated || !token) {
            setIsLoading(false);
            return;
        }

        try {
            const headers = getAuthHeaders();
            const response = await axios.get(`${BACKEND_URL}/api/projects?limit=1`, { headers });
            
            if (response.data && response.data.length > 0) {
                // Fetch full project details to get continue_builds
                const fullProject = await axios.get(
                    `${BACKEND_URL}/api/projects/${response.data[0].id}`, 
                    { headers }
                );
                setRecentProject(fullProject.data);
            }
        } catch (error) {
            console.error('Failed to fetch recent project:', error);
        } finally {
            setIsLoading(false);
        }
    }, [isAuthenticated, token, getAuthHeaders]);

    useEffect(() => {
        // Check if user dismissed today
        const dismissedToday = localStorage.getItem('vcc_resume_dismissed');
        if (dismissedToday) {
            const dismissDate = new Date(dismissedToday);
            const now = new Date();
            // Reset after 12 hours
            if (now - dismissDate < 12 * 60 * 60 * 1000) {
                setIsDismissed(true);
                setIsLoading(false);
                return;
            }
        }
        
        fetchRecentProject();
    }, [fetchRecentProject]);

    const handleDismiss = () => {
        setIsDismissed(true);
        localStorage.setItem('vcc_resume_dismissed', new Date().toISOString());
    };

    const handleContinue = () => {
        if (recentProject) {
            onContinue(recentProject);
        }
    };

    const formatTimeAgo = (dateString) => {
        const date = new Date(dateString);
        const now = new Date();
        const diffMs = now - date;
        const diffMins = Math.floor(diffMs / (1000 * 60));
        const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
        const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

        if (diffMins < 1) return 'Just now';
        if (diffMins < 60) return `${diffMins}m ago`;
        if (diffHours < 24) return `${diffHours}h ago`;
        if (diffDays === 1) return 'Yesterday';
        if (diffDays < 7) return `${diffDays} days ago`;
        return date.toLocaleDateString();
    };

    const getLastAction = () => {
        if (!recentProject) return null;
        
        const builds = recentProject.continue_builds || [];
        if (builds.length > 0) {
            const lastBuild = builds[builds.length - 1];
            return ACTION_CONFIG[lastBuild.option] || ACTION_CONFIG.blueprint;
        }
        
        if (recentProject.blueprint) {
            return ACTION_CONFIG.blueprint;
        }
        
        return null;
    };

    // Don't show if loading, dismissed, not authenticated, or no project
    if (isLoading || isDismissed || !isAuthenticated || !recentProject) {
        return null;
    }

    const lastAction = getLastAction();
    const ActionIcon = lastAction?.icon || Sparkles;
    const showUpgradePrompt = hitPaywall && !isPro;

    return (
        <AnimatePresence>
            <motion.div
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                className="mb-6"
                data-testid="resume-card"
            >
                <div className={`
                    relative p-4 rounded-xl border
                    ${showUpgradePrompt 
                        ? 'bg-gradient-to-r from-violet-900/30 via-surface to-fuchsia-900/20 border-violet-500/30' 
                        : 'bg-surface border-white/10'
                    }
                `}>
                    {/* Dismiss button */}
                    <button
                        onClick={handleDismiss}
                        className="absolute top-3 right-3 p-1 rounded-lg hover:bg-white/10 transition-colors text-gray-500 hover:text-gray-300"
                        data-testid="resume-dismiss"
                    >
                        <X className="w-4 h-4" />
                    </button>

                    <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                        {/* Project info */}
                        <div className="flex items-start gap-3 flex-1 min-w-0">
                            <div className={`p-2.5 rounded-lg ${showUpgradePrompt ? 'bg-violet-500/20' : 'bg-white/5'}`}>
                                <FolderOpen className={`w-5 h-5 ${showUpgradePrompt ? 'text-violet-400' : 'text-gray-400'}`} />
                            </div>
                            <div className="flex-1 min-w-0">
                                <p className="text-xs text-gray-500 mb-0.5">Continue where you left off</p>
                                <h3 className="font-heading font-medium text-white truncate">
                                    {recentProject.name}
                                </h3>
                                <div className="flex items-center gap-3 mt-1.5 text-xs text-gray-500">
                                    <span className="flex items-center gap-1">
                                        <Clock className="w-3 h-3" />
                                        {formatTimeAgo(recentProject.updated_at)}
                                    </span>
                                    {lastAction && (
                                        <span className={`flex items-center gap-1 ${lastAction.color}`}>
                                            <ActionIcon className="w-3 h-3" />
                                            {lastAction.label}
                                        </span>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Actions */}
                        <div className="flex items-center gap-2 sm:flex-shrink-0">
                            {showUpgradePrompt ? (
                                <>
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={onViewProjects}
                                        className="text-gray-400 hover:text-white"
                                        data-testid="resume-view-projects"
                                    >
                                        View Projects
                                    </Button>
                                    <Button
                                        size="sm"
                                        onClick={onUpgrade}
                                        className="bg-gradient-to-r from-violet-600 to-fuchsia-600 hover:from-violet-500 hover:to-fuchsia-500"
                                        data-testid="resume-upgrade"
                                    >
                                        <Crown className="w-4 h-4 mr-1.5" />
                                        Unlock Pro
                                    </Button>
                                </>
                            ) : (
                                <>
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={onViewProjects}
                                        className="text-gray-400 hover:text-white"
                                        data-testid="resume-view-projects"
                                    >
                                        View Projects
                                    </Button>
                                    <Button
                                        size="sm"
                                        onClick={handleContinue}
                                        className="bg-violet-600 hover:bg-violet-500"
                                        data-testid="resume-continue"
                                    >
                                        Continue Building
                                        <ArrowRight className="w-4 h-4 ml-1.5" />
                                    </Button>
                                </>
                            )}
                        </div>
                    </div>

                    {/* Pro upsell message */}
                    {showUpgradePrompt && (
                        <div className="mt-3 pt-3 border-t border-white/10">
                            <p className="text-xs text-violet-300/80">
                                <Crown className="w-3 h-3 inline mr-1" />
                                You hit the daily limit last time. Upgrade to Pro for 30 blueprints/day and unlimited Continue Building.
                            </p>
                        </div>
                    )}
                </div>
            </motion.div>
        </AnimatePresence>
    );
};
